/**
 * Document revision info for Working Document details dialog.
 * Calls api/get-document-revision-info; state: documentRevisionData, loading, message.
 */
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { getDocumentDetails } from "../../api/documentsAPI";
import UserAuth from "../../../auth/userAuth";
import { logger } from "../../../utils/logger";
import { onGetFormatsByClass } from "../docManAction";
import { onCreateViewCopyChange, onDocClassFormatChange } from "../toolsAction";
import { normalizeDocumentRevisionInfoResponse } from "../../../utils/normalizeDocumentRevisionInfoResponse";
import { resolveCreateViewCopyDefault } from "../../../utils/resolveCreateViewCopyDefault";

const userAuth = new UserAuth();

/** Matches IFS DocMan NotFound for GetDocumentRevisionInfo (e.g. document deleted in IFS). */
const DOCUMENT_REVISION_NOT_IN_IFS =
  "Document is not available in IFS";

function mapDocumentRevisionInfoUserMessage(raw) {
  const s =
    typeof raw === "string"
      ? raw
      : raw != null && typeof raw === "object" && "message" in raw
        ? String(raw.message ?? "")
        : String(raw ?? "");
  if (!s) return DOCUMENT_REVISION_NOT_IN_IFS;
  if (
    /GetDocumentRevisionInfo/i.test(s) &&
    (/NotFound/i.test(s) || /Resource not found/i.test(s))
  ) {
    return DOCUMENT_REVISION_NOT_IN_IFS;
  }
  return s;
}

/**
 * Fetch document revision info (api/get-document-revision-info).
 * Uses the working file's own revision (not IFS latest) so Details matches the list Document No.
 * @param {object} file - Document/file object with id, DriveId, and optional docClass, docNo, docSheet, docRev
 */
export const fetchDocumentRevisionInfo = createAsyncThunk(
  "documents/documentRevisionInfo/fetch",
  async (file, { rejectWithValue, dispatch, getState }) => {
    logger.info("fetchDocumentRevisionInfo", { fileId: file?.id }, "REDUX");
    try {
      const tokenResponse = await userAuth.getUserToken(true);
      const accessToken = tokenResponse?.accessToken ?? null;
      if (!accessToken) {
        return rejectWithValue({ message: "No access token" });
      }
      const { data, status } = await getDocumentDetails(file, accessToken, {
        latestRevision: false,
      });

      const { revision, isViewCopyExists: viewCopyFlag } =
        normalizeDocumentRevisionInfoResponse(data);

      // Fetch up-to-date document class rules (like viewRequired) for the document
      const docClass =
        revision?.docClass ??
        data?.results?.docClass ??
        data?.results?.DocClass ??
        data?.docClass;
      if (status === 200 && docClass) {
        dispatch(onGetFormatsByClass(docClass))
          .then((payload) => {
            if (payload) {
              const classItems = Array.isArray(payload)
                ? payload
                : payload?.classDetails ??
                  payload?.ClassDetails ??
                  payload?.value?.classDetails ??
                  payload?.value?.ClassDetails ??
                  [];

              if (Array.isArray(classItems) && classItems.length > 0) {
                const classItem = classItems[0];
                const apiViewRequired = `${classItem?.viewRequired ?? classItem?.ViewRequired ?? "N"}`;
                const apiFormatMandatory = `${classItem?.formatMandatory ?? classItem?.FormatMandatory ?? "N"}`;
                const apiObjConnectionRequired = `${classItem?.objectConnectionRequired ?? classItem?.ObjectConnectionRequired ?? "N"}`;

                const state = getState();
                const checkin = state?.CheckinDocument;
                const uploadFiles = checkin?.uploadFiles ?? [];
                const uploadViewCopyCount = checkin?.uploadViewCopyCount ?? 0;

                const fileExt = (
                  revision?.FileExtension ??
                  revision?.fileExtension ??
                  ""
                ).toUpperCase();
                const isWordLike =
                  fileExt === "DOCX" || fileExt === "DOC" || fileExt === "DOCM" || fileExt === "DOTM";

                const ifsViewCopyKnown = typeof viewCopyFlag === "boolean";

                dispatch(
                  onDocClassFormatChange(
                    docClass,
                    "",
                    apiViewRequired,
                    apiFormatMandatory,
                    apiObjConnectionRequired,
                    uploadFiles,
                    uploadViewCopyCount,
                    // Working-document check-in: default auto-generate from revision type, not bulk rules.
                    { skipCreateViewCopyDefault: true },
                  ),
                );

                if (uploadViewCopyCount === 0) {
                  dispatch(
                    onCreateViewCopyChange(
                      resolveCreateViewCopyDefault({
                        isViewCopyExists: viewCopyFlag,
                        viewRequired: apiViewRequired,
                        isWordLike,
                        uploadViewCopyCount,
                      }),
                    ),
                  );
                }
              }
            }
          })
          .catch((err) => {
            logger.error("Failed to fetch format info in fetchDocumentRevisionInfo:", err, "REDUX");
          });
      }

      return {
        data: data ?? {},
        status,
        isViewCopyExists: viewCopyFlag,
        revision,
      };
    } catch (err) {
      logger.error("fetchDocumentRevisionInfo failed", err, "REDUX");
      const raw =
        err?.response?.data?.message ??
        (typeof err?.response?.data === "string" ? err.response.data : null) ??
        err?.message ??
        "Failed to load document details.";
      return rejectWithValue({ message: mapDocumentRevisionInfoUserMessage(raw) });
    }
  }
);

const initialState = {
  documentRevisionData: {},
  /** From IFS VIEW file lookup; null when API did not return the flag (legacy). */
  isViewCopyExists: null,
  loading: false,
  message: null,
};

const documentRevisionInfoSlice = createSlice({
  name: "documents/documentRevisionInfo",
  initialState,
  reducers: {
    clearDocumentRevisionInfo: (state) => {
      state.documentRevisionData = {};
      state.isViewCopyExists = null;
      state.loading = false;
      state.message = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchDocumentRevisionInfo.pending, (state) => {
        state.loading = true;
        state.message = null;
        state.documentRevisionData = {};
        state.isViewCopyExists = null;
      })
      .addCase(fetchDocumentRevisionInfo.fulfilled, (state, action) => {
        const { status, revision, isViewCopyExists } = action.payload ?? {};
        state.loading = false;
        state.message = null;
        if (status === 200) {
          state.documentRevisionData =
            revision && typeof revision === "object" ? revision : {};
          state.isViewCopyExists =
            typeof isViewCopyExists === "boolean" ? isViewCopyExists : null;
        } else if (status === 204) {
          state.documentRevisionData = {};
          state.isViewCopyExists = null;
          state.message = "No document details found.";
        } else {
          state.documentRevisionData = {};
          state.isViewCopyExists = null;
          state.message = mapDocumentRevisionInfoUserMessage(
            `Request returned status ${status}.`,
          );
        }
      })
      .addCase(fetchDocumentRevisionInfo.rejected, (state, action) => {
        state.loading = false;
        state.documentRevisionData = {};
        state.isViewCopyExists = null;
        state.message =
          action.payload?.message ?? action.error?.message ?? "Failed to load document details.";
      });
  },
});

export const { clearDocumentRevisionInfo } = documentRevisionInfoSlice.actions;
export default documentRevisionInfoSlice.reducer;
